const keys = [
  'manualAccount',
  'holder',
  'iban',
  'bank',
  'currency',
  'accountingAccount',
  'save',
  'importStatement',
  'account',
  'format',
  'profile',
  'file',
  'preview',
  'confirm',
  'history',
  'download',
  'transactions',
  'duplicates',
  'invalid',
  'noHistory',
  'mappingHint',
  'directory',
  'directoryHint',
  'relativeDirectory',
  'enabled',
  'run',
  'failure',
  'success',
  'profileName',
  'bookingDate',
  'amount',
  'reference',
  'purpose',
  'counterparty',
  'createProfile',
  'newProfile',
  'valueDate',
  'direction',
  'counterpartyIban',
  'counterpartyBic',
  'endToEndId',
  'mandateReference',
  'creditorId',
  'bankTransactionCode',
  'balanceAfter',
  'directoryUnavailable',
  'fileFailureHint',
  'ambiguousSource',
  'sourceReview',
  'sourceRow',
  'sourceExisting',
  'sourceNew',
  'sourceReviewChanged',
  'sourceReviewHint',
  'retry',
  'retrySelected',
  'attemptLabel',
  'retryOfLabel',
  'retryNotFound',
  'retryChanged',
  'archiveOverlap',
] as const;
function labels(values: readonly string[]) {
  if (values.length !== keys.length)
    throw new Error('Bank import translations must cover every field.');
  return Object.fromEntries(
    keys.map((key, index) => {
      const label = values[index];
      if (!label) throw new Error(`Missing bank import label: ${key}`);
      return [key, label];
    }),
  );
}
export const bankImportI18n = {
  de: labels([
    'Manuelles Bankkonto',
    'Kontoinhaber',
    'IBAN',
    'Bankname',
    'Währung',
    'Buchhaltungskonto',
    'Speichern',
    'Kontoauszug importieren',
    'Bankkonto',
    'Dateiformat',
    'CSV-Profil',
    'Datei',
    'Vorschau prüfen',
    'Import bestätigen',
    'Importverlauf',
    'Original herunterladen',
    'Umsätze',
    'Dubletten',
    'Bitte prüfen Sie die markierten Angaben.',
    'Noch keine Kontoauszüge importiert.',
    'Ordnen Sie die exakten Spaltenüberschriften zu. Ohne Kopfzeile verwenden Sie die Spaltennummer ab 0. Negative Beträge sind Ausgänge.',
    'Verzeichnisimport',
    'Dateien werden innerhalb des vom Betreiber freigegebenen Eingangsverzeichnisses gelesen. Originale und fehlerhafte Dateien bleiben erhalten. Es werden nur Vorschläge erzeugt.',
    'Relatives Eingangsverzeichnis',
    'Aktiviert',
    'Jetzt prüfen',
    'Fehlgeschlagen',
    'Gespeichert',
    'Profilname',
    'Buchungsdatum',
    'Betrag',
    'Bankreferenz',
    'Verwendungszweck',
    'Gegenpartei',
    'Profil speichern',
    'Neues CSV-Profil',
    'Valutadatum',
    'Soll/Haben',
    'IBAN der Gegenpartei',
    'BIC der Gegenpartei',
    'Ende-zu-Ende-Referenz',
    'Mandatsreferenz',
    'Gläubiger-ID',
    'Geschäftsvorfallcode',
    'Saldo nach Umsatz',
    'Der Betreiber hat noch kein Eingangsverzeichnis eingerichtet.',
    'Prüfen Sie Dateiformat, Daten, Größe und Zugriffsrechte. Fehlerhafte Dateien bleiben erhalten.',
    'Überlappende Datei ohne eindeutige Bankreferenz: Bitte prüfen Sie die Quelle und ordnen Sie eine stabile Referenz zu. Es wurden keine zusätzlichen Umsätze übernommen.',
    'Unklare Quellumsätze prüfen',
    'Umsatz {{number}}',
    'Vorhandenem Umsatz zuordnen',
    'Bewusst als zusätzlichen neuen Umsatz übernehmen',
    'Die Datei oder die möglichen Zuordnungen haben sich geändert. Bitte erstellen Sie eine neue Vorschau.',
    'Entscheiden Sie je Umsatz anhand der Originale: einen vorhandenen Umsatz verknüpfen oder einen zusätzlichen neuen Umsatz übernehmen. Prüfen Sie anschließend die Vorschau erneut. Ihre Entscheidung wird protokolliert.',
    'Import erneut versuchen',
    'Neuer Versuch für {{file}}: Wählen Sie dieselbe Originaldatei erneut aus und prüfen Sie die aktuelle Vorschau. Der fehlgeschlagene Versuch bleibt erhalten.',
    'Versuch {{number}}',
    'Vorheriger fehlgeschlagener Versuch',
    'Der fehlgeschlagene Import wurde nicht gefunden.',
    'Datei oder Importstand haben sich geändert. Wählen Sie den aktuellen fehlgeschlagenen Versuch und prüfen Sie die Vorschau erneut.',
    'Das ZIP-Archiv enthält möglicherweise überlappende Auszüge ohne eindeutige Bankreferenz. Entpacken Sie das Archiv und prüfen Sie jede Datei einzeln in der Vorschau, bevor Sie sie importieren.',
  ]),
  en: labels([
    'Manual bank account',
    'Account holder',
    'IBAN',
    'Bank name',
    'Currency',
    'Ledger account',
    'Save',
    'Import statement',
    'Bank account',
    'File format',
    'CSV profile',
    'File',
    'Review preview',
    'Confirm import',
    'Import history',
    'Download original',
    'Transactions',
    'Duplicates',
    'Review the highlighted fields.',
    'No statements imported yet.',
    'Map exact column headers. Without a header use column numbers starting at 0. Negative amounts are outgoing.',
    'Directory import',
    'Files are read inside the input directory approved by the operator. Originals and failed files are retained. Only suggestions are generated.',
    'Relative input directory',
    'Enabled',
    'Check now',
    'Failed',
    'Saved',
    'Profile name',
    'Booking date',
    'Amount',
    'Bank reference',
    'Remittance',
    'Counterparty',
    'Save profile',
    'New CSV profile',
    'Value date',
    'Debit/credit',
    'Counterparty IBAN',
    'Counterparty BIC',
    'End-to-end reference',
    'Mandate reference',
    'Creditor ID',
    'Transaction code',
    'Balance after transaction',
    'The operator has not configured an input directory yet.',
    'Check the file format, data, size and access permissions. Failed files are retained.',
    'Overlapping file without a unique bank reference: review the source and map a stable reference. No additional entries were imported.',
    'Review uncertain source entries',
    'Entry {{number}}',
    'Link existing entry',
    'Deliberately import as an additional new entry',
    'The file or available candidates changed. Generate a new preview.',
    'Compare the originals and choose an existing entry or an additional new entry for each row. Then review the preview again. Your decision is audited.',
    'Retry import',
    'New attempt for {{file}}: select the same original file again and review the current preview. The failed attempt is retained.',
    'Attempt {{number}}',
    'Previous failed attempt',
    'The failed import was not found.',
    'The file or import state changed. Select the latest failed attempt and review the preview again.',
    'The ZIP contains potentially overlapping statements without a unique bank reference. Extract the archive and review each individual file in the preview before importing.',
  ]),
  tr: labels([
    'Manuel banka hesabı',
    'Hesap sahibi',
    'IBAN',
    'Banka adı',
    'Para birimi',
    'Muhasebe hesabı',
    'Kaydet',
    'Hesap ekstresi içe aktar',
    'Banka hesabı',
    'Dosya biçimi',
    'CSV profili',
    'Dosya',
    'Önizlemeyi incele',
    'İçe aktarımı onayla',
    'İçe aktarma geçmişi',
    'Orijinali indir',
    'Hareketler',
    'Yinelenenler',
    'İşaretli alanları kontrol edin.',
    'Henüz ekstre içe aktarılmadı.',
    'Tam sütun başlıklarını eşleyin. Başlık yoksa 0 ile başlayan sütun numaralarını kullanın. Negatif tutarlar çıkıştır.',
    'Dizin içe aktarma',
    'Dosyalar işletmecinin onayladığı giriş dizininde okunur. Orijinaller ve hatalı dosyalar korunur. Yalnızca öneri oluşturulur.',
    'Göreli giriş dizini',
    'Etkin',
    'Şimdi kontrol et',
    'Başarısız',
    'Kaydedildi',
    'Profil adı',
    'Kayıt tarihi',
    'Tutar',
    'Banka referansı',
    'Açıklama',
    'Karşı taraf',
    'Profili kaydet',
    'Yeni CSV profili',
    'Valör tarihi',
    'Borç/alacak',
    'Karşı taraf IBAN',
    'Karşı taraf BIC',
    'Uçtan uca referans',
    'Yetki referansı',
    'Alacaklı kimliği',
    'İşlem kodu',
    'İşlem sonrası bakiye',
    'İşletmeci henüz giriş dizini yapılandırmadı.',
    'Dosya biçimini, verileri, boyutu ve erişim izinlerini kontrol edin. Hatalı dosyalar korunur.',
    'Benzersiz banka referansı olmayan örtüşen dosya: kaynağı inceleyin ve kararlı referans eşleyin. Ek hareket içe aktarılmadı.',
    'Belirsiz kaynak hareketlerini inceleyin',
    'Hareket {{number}}',
    'Mevcut harekete bağla',
    'Bilinçli olarak ek yeni hareket olarak içe aktar',
    'Dosya veya adaylar değişti. Yeni önizleme oluşturun.',
    'Orijinalleri karşılaştırın ve her satır için mevcut hareketi veya ek yeni hareketi seçin. Ardından önizlemeyi tekrar kontrol edin. Kararınız denetim kaydına alınır.',
    'İçe aktarmayı yeniden dene',
    '{{file}} için yeni deneme: aynı orijinal dosyayı seçin ve güncel önizlemeyi inceleyin. Başarısız deneme korunur.',
    'Deneme {{number}}',
    'Önceki başarısız deneme',
    'Başarısız içe aktarma bulunamadı.',
    'Dosya veya içe aktarma durumu değişti. Son başarısız denemeyi seçip önizlemeyi tekrar inceleyin.',
    'ZIP, benzersiz banka referansı olmayan ve örtüşebilecek ekstreler içeriyor. Arşivi açın ve içe aktarmadan önce her dosyayı önizlemede ayrı ayrı inceleyin.',
  ]),
  fr: labels([
    'Compte bancaire manuel',
    'Titulaire',
    'IBAN',
    'Nom de la banque',
    'Devise',
    'Compte comptable',
    'Enregistrer',
    'Importer un relevé',
    'Compte bancaire',
    'Format de fichier',
    'Profil CSV',
    'Fichier',
    'Examiner l’aperçu',
    'Confirmer l’import',
    'Historique des imports',
    'Télécharger l’original',
    'Opérations',
    'Doublons',
    'Vérifiez les champs signalés.',
    'Aucun relevé importé.',
    'Associez les en-têtes exacts. Sans en-tête, utilisez les numéros de colonne à partir de 0. Les montants négatifs sont des sorties.',
    'Import de répertoire',
    'Les fichiers sont lus dans le répertoire autorisé par l’exploitant. Les originaux et les fichiers en échec sont conservés. Seules des propositions sont créées.',
    'Répertoire d’entrée relatif',
    'Activé',
    'Vérifier maintenant',
    'Échec',
    'Enregistré',
    'Nom du profil',
    'Date comptable',
    'Montant',
    'Référence bancaire',
    'Libellé',
    'Contrepartie',
    'Enregistrer le profil',
    'Nouveau profil CSV',
    'Date de valeur',
    'Débit/crédit',
    'IBAN de la contrepartie',
    'BIC de la contrepartie',
    'Référence de bout en bout',
    'Référence du mandat',
    'Identifiant créancier',
    'Code opération',
    'Solde après opération',
    'L’exploitant n’a pas encore configuré de répertoire d’entrée.',
    'Vérifiez le format, les données, la taille et les droits d’accès. Les fichiers en échec sont conservés.',
    'Fichier recouvrant des opérations sans référence bancaire unique : vérifiez la source et associez une référence stable. Aucune opération supplémentaire n’a été importée.',
    'Vérifier les opérations sources incertaines',
    'Opération {{number}}',
    'Associer à une opération existante',
    'Importer volontairement une nouvelle opération supplémentaire',
    'Le fichier ou les propositions ont changé. Créez un nouvel aperçu.',
    'Comparez les originaux et choisissez une opération existante ou une nouvelle opération supplémentaire pour chaque ligne. Vérifiez ensuite à nouveau l’aperçu. La décision est journalisée.',
    'Réessayer l’import',
    'Nouvelle tentative pour {{file}} : sélectionnez le même original et vérifiez l’aperçu actuel. La tentative en échec est conservée.',
    'Tentative {{number}}',
    'Tentative précédente en échec',
    'L’import en échec est introuvable.',
    'Le fichier ou l’état de l’import a changé. Sélectionnez la dernière tentative en échec et vérifiez à nouveau l’aperçu.',
    'Le ZIP contient des relevés susceptibles de se chevaucher sans référence bancaire unique. Extrayez l’archive et vérifiez chaque fichier séparément dans l’aperçu avant de l’importer.',
  ]),
  es: labels([
    'Cuenta bancaria manual',
    'Titular',
    'IBAN',
    'Nombre del banco',
    'Moneda',
    'Cuenta contable',
    'Guardar',
    'Importar extracto',
    'Cuenta bancaria',
    'Formato de archivo',
    'Perfil CSV',
    'Archivo',
    'Revisar vista previa',
    'Confirmar importación',
    'Historial de importación',
    'Descargar original',
    'Movimientos',
    'Duplicados',
    'Revise los campos marcados.',
    'Aún no se han importado extractos.',
    'Asigne las cabeceras exactas. Sin cabecera, utilice números de columna desde 0. Los importes negativos son salidas.',
    'Importación de directorio',
    'Los archivos se leen dentro del directorio autorizado por el operador. Se conservan los originales y los archivos fallidos. Solo se generan sugerencias.',
    'Directorio de entrada relativo',
    'Activado',
    'Comprobar ahora',
    'Error',
    'Guardado',
    'Nombre del perfil',
    'Fecha contable',
    'Importe',
    'Referencia bancaria',
    'Concepto',
    'Contraparte',
    'Guardar perfil',
    'Nuevo perfil CSV',
    'Fecha valor',
    'Debe/haber',
    'IBAN de la contraparte',
    'BIC de la contraparte',
    'Referencia de extremo a extremo',
    'Referencia de mandato',
    'Identificador del acreedor',
    'Código de operación',
    'Saldo tras el movimiento',
    'El operador aún no ha configurado un directorio de entrada.',
    'Revise el formato, los datos, el tamaño y los permisos. Se conservan los archivos fallidos.',
    'Archivo solapado sin referencia bancaria única: revise el origen y asigne una referencia estable. No se importaron movimientos adicionales.',
    'Revisar movimientos de origen incierto',
    'Movimiento {{number}}',
    'Vincular movimiento existente',
    'Importar conscientemente como movimiento nuevo adicional',
    'El archivo o los candidatos han cambiado. Genere otra vista previa.',
    'Compare los originales y elija un movimiento existente o uno nuevo adicional para cada fila. Después revise de nuevo la vista previa. La decisión queda registrada.',
    'Reintentar importación',
    'Nuevo intento para {{file}}: seleccione el mismo original y revise la vista previa actual. Se conserva el intento fallido.',
    'Intento {{number}}',
    'Intento fallido anterior',
    'No se encontró la importación fallida.',
    'El archivo o estado de importación cambió. Seleccione el último intento fallido y revise de nuevo la vista previa.',
    'El ZIP contiene extractos que pueden solaparse sin una referencia bancaria única. Extraiga el archivo y revise cada fichero por separado en la vista previa antes de importarlo.',
  ]),
};
